import type { routes } from '@semios/app-platform-banyan-route-definitions'
import type { VV } from '@semios/app-platform-value-type-definitions'
import { blockLacksCropSectionMaker } from 'App/Map/PanelDetails/_utils/blockLacksCropSectionMaker'
import { getTimezoneForSelectedPropertyOrRegion } from 'App/Map/PanelDetails/_utils/getTimezoneForSelectedPropertyOrRegion'
import { propertyLacksPermissionSectionMaker } from 'App/Map/PanelDetails/_utils/propertyLacksPermissionSectionMaker'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import { DropdownSelectorBlock } from 'App/Map/PanelDetails/SectionTitleBars/DropdownSelectorBlock/DropdownSelectorBlock'
import type { StackedChartSection } from 'components/StackedChart/types'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TSelectedFieldAssetsStoreState } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import type { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { doesSelectedBlockHaveValueTypes } from 'utils/doesSelectedFieldAssetHaveValueTypes'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { GrapePowderyMildewTooltipModal } from '../../../../../_utils/by-domain/powderyMildew/PowderyMildewTooltipModal'
import type { TPowderyMildewPlotLine } from '../../_utils/powderyMildewChartHelper'
import { secondaryInfectionRiskChartHelper } from '../../_utils/powderyMildewChartHelper'

type TValueTypeKey = 'grapePowderyMildewModelPredictedRisk'

const preferredAggregationInterval = { preferredAggregationInterval: 'DAILY' } as const

const getValuesRequestedForBlocks = ({
  selectedFieldAssets,
  selectedValueGroups,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}): Partial<routes.Values.Request> => {
  if (!selectedFieldAssets.block) return {}

  let valuesRequested: Partial<
    Record<
      VV.DomainTypes.GrapePowderyMildew.TTimeseriesValueTypeKeysMerged,
      typeof preferredAggregationInterval
    >
  > = {}

  Object.entries(selectedValueGroups).forEach(([valueGroup, isActive]) => {
    if (!!isActive && valueGroup.includes('grape_powdery_mildew')) {
      if (!selectedPropertyHasPermission({ permission: 'VIEW_GRAPE_POWDERY_MILDEW_DETAILS' })) return

      valuesRequested = {
        grapePowderyMildewModelPredictedRisk: preferredAggregationInterval,
      }
    }
  })

  if (Object.keys(valuesRequested).length === 0) return {}

  const valuesToBeRequested: Partial<routes.Values.Request> = {}

  if (
    Object.keys(valuesRequested).length &&
    doesSelectedBlockHaveValueTypes({ valuesTimeseries: Object.keys(valuesRequested) as TValueTypeKey[] })
  )
    valuesToBeRequested.blocks = {
      blockIds: [selectedFieldAssets.block],
      valuesRequested,
    }

  return valuesToBeRequested
}

export const apiArgs = ({
  selectedValueGroups,
  selectedFieldAssets,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}) => {
  const blockValues = getValuesRequestedForBlocks({ selectedFieldAssets, selectedValueGroups })

  return { ...blockValues }
}

export const content = ({
  data,
  compareSeasonsData,
}: {
  data: routes.Values.Response
  compareSeasonsData: routes.Values.Response
}): StackedChartSection => {
  const { compareSeasonsInterval } = detailsPanelStore.getState()
  const selectedFieldAssets = selectedFieldAssetsStore.getState()
  const selectedProperty = selectedFieldAssets.property
  const blockId = String(selectedFieldAssets.block)
  const timezone = getTimezoneForSelectedPropertyOrRegion()
  const property = fieldAssetStore.getState()?.properties?.[Number(selectedProperty)]

  const hasPermission = selectedPropertyHasPermission({
    permission: 'VIEW_GRAPE_POWDERY_MILDEW_DETAILS',
  })

  const valueTypeKey = 'grapePowderyMildewModelPredictedRisk'

  const commonReturnItems = {
    title: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{unitConverter.grapePowderyMildew().titleWithoutUnit()}</span>
        <GrapePowderyMildewTooltipModal
          includeWesternUSAContent={Boolean(
            property &&
            property.country === 'US' &&
            ['CA', 'WA'].includes(property.gAdministrativeAreaLevel1 ?? ''),
          )}
        />
      </div>
    ),
    fieldAssetPicker: (
      <DropdownSelectorBlock valuesTimeseriesToFilterOn={[valueTypeKey]} showAssociatedWeatherStationName />
    ),
    id: `stackem-grape-powdery-mildew-risk-${blockId}`,
  }

  if (!hasPermission && selectedProperty) {
    return propertyLacksPermissionSectionMaker(commonReturnItems)
  }

  const blockHasValueTypes = doesSelectedBlockHaveValueTypes({ valuesTimeseries: [valueTypeKey] })

  if (!blockHasValueTypes && selectedProperty) {
    return blockLacksCropSectionMaker({
      ...commonReturnItems,
      translatedCropName: translate.phrases.dbCropsName('grape'),
    })
  }

  const allForecastMoments = [
    data?.blocks?.[blockId]?.values?.[valueTypeKey]?.[0]?.metadata?.forecastStartsAt,
  ]
    .filter(Boolean)
    .map((d) => moment.tz(d, timezone))

  const firstForecastTimestamp = allForecastMoments.length ? +moment.min(allForecastMoments) : +new Date()
  const plotLines: TPowderyMildewPlotLine[] = []
  const metadata = data?.blocks?.[blockId]?.values?.[valueTypeKey]?.[0]?.metadata

  if (metadata) {
    const { budBurstDates, modelPrimaryInfectionDates } = metadata

    budBurstDates.forEach((date) => {
      plotLines.push({
        id: `budburst-${date}`,
        value: +moment.tz(date, timezone),
        label: translate.phrases.banyanApp('Budbreak Date'),
      })
    })

    modelPrimaryInfectionDates.forEach((date) => {
      plotLines.push({
        id: `modelPrimaryInfection-${date}`,
        value: +moment.tz(date, timezone),
        label: translate.phrases.banyanApp('Model Predicted Primary Infection Date'),
      })
    })
  }

  const compareSeasonsMetadata = compareSeasonsData?.blocks?.[blockId]?.values?.[valueTypeKey]?.[0]?.metadata

  if (compareSeasonsMetadata) {
    const { budBurstDates, modelPrimaryInfectionDates } = compareSeasonsMetadata

    budBurstDates.forEach((date) => {
      plotLines.push({
        id: `compareSeasons-budburst-${date}`,
        value: +moment.tz(date, timezone).add(compareSeasonsInterval, 'years'),
        label: translate.phrases.banyanApp('Budbreak Date'),
        isCompareSeasons: true,
      })
    })

    modelPrimaryInfectionDates.forEach((date) => {
      plotLines.push({
        id: `compareSeasons-modelPrimaryInfection-${date}`,
        value: +moment.tz(date, timezone).add(compareSeasonsInterval, 'years'),
        label: translate.phrases.banyanApp('Model Predicted Primary Infection Date'),
        isCompareSeasons: true,
      })
    })
  }

  const chartConfig = secondaryInfectionRiskChartHelper({
    data: (data?.blocks?.[blockId]?.values?.[valueTypeKey]?.[0]?.timeseries ?? []).map(
      (d: { timestamp: string; value: number | null }) => [
        +new Date(d.timestamp),
        unitConverter.grapePowderyMildew(d.value).value(),
      ],
    ),
    compareSeasonsData: compareSeasonsInterval
      ? (compareSeasonsData?.blocks?.[blockId]?.values?.[valueTypeKey]?.[0]?.timeseries ?? []).map(
          (d: { timestamp: string; value: number | null }) => [
            +new Date(d.timestamp),
            unitConverter.grapePowderyMildew(d.value).value(),
          ],
        )
      : undefined,
    compareSeasonsInterval,
    forecastStartsAt: firstForecastTimestamp,
    seriesId: 'grapePowderyMildewModelPredictedRisk',
    seriesName: translate.phrases.banyanApp('Model Predicted Risk'),
    seriesTooltip: {
      valueDecimals: unitConverter.grapePowderyMildew().defaultNumberOfDecimalPlaces(),
      valueSuffix: ` ${unitConverter.grapePowderyMildew().suffix()}`,
    },
    plotLines,
  })

  return {
    ...commonReturnItems,
    items: [
      {
        chartConfig,
      },
    ],
  }
}
